/**
 * Windows-style in-window application menu bars.
 * Apps register menus via ctx.shell.menus.set([...]) for their window.
 * Menu bars render under the title bar inside WindowFrame (not top OS menubar).
 */
import { useEffect, useState } from "react";

export type AppMenuItem = {
  id: string;
  label?: string;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  /** Visual separator (ignore label) */
  separator?: boolean;
  action?: () => void;
  submenu?: AppMenuItem[];
};

export type AppMenu = {
  id: string;
  label: string;
  items: AppMenuItem[];
};

export type AppMenuBar = AppMenu[];

type Listener = () => void;

class AppMenuStore {
  /** windowId → bar */
  private bars = new Map<string, AppMenuBar>();
  private listeners = new Set<Listener>();
  openMenuId: string | null = null;
  openWindowId: string | null = null;

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const fn of this.listeners) fn();
  }

  get(windowId: string): AppMenuBar {
    return this.bars.get(windowId) || [];
  }

  set(windowId: string, bar: AppMenuBar) {
    this.bars.set(windowId, bar || []);
    this.emit();
  }

  clear(windowId: string) {
    this.bars.delete(windowId);
    if (this.openWindowId === windowId) {
      this.openMenuId = null;
      this.openWindowId = null;
    }
    this.emit();
  }

  setOpen(windowId: string | null, menuId: string | null) {
    this.openWindowId = windowId;
    this.openMenuId = menuId;
    this.emit();
  }

  closeOpen() {
    if (!this.openMenuId) return;
    this.openMenuId = null;
    this.openWindowId = null;
    this.emit();
  }
}

export const appMenuStore = new AppMenuStore();

export function useAppMenuBar(windowId: string): AppMenuBar {
  const [bar, setBar] = useState(() => appMenuStore.get(windowId));
  useEffect(() => {
    setBar(appMenuStore.get(windowId));
    return appMenuStore.subscribe(() => setBar(appMenuStore.get(windowId)));
  }, [windowId]);
  return bar;
}

export function useAppMenuOpen(): { windowId: string | null; menuId: string | null } {
  const [state, setState] = useState({
    windowId: appMenuStore.openWindowId as string | null,
    menuId: appMenuStore.openMenuId as string | null,
  });
  useEffect(
    () =>
      appMenuStore.subscribe(() =>
        setState({
          windowId: appMenuStore.openWindowId,
          menuId: appMenuStore.openMenuId,
        })
      ),
    []
  );
  return state;
}
